import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  getProducts,
  getCategories,
  getCustomers,
  createOrder,
  updateOrder,
  getNextInvoiceNumber,
  createProduct,
  updateOrderStatus,
  CompanySettings,
  DEFAULT_COMPANY_SETTINGS,
  getCompanySettings,
} from '@/services/firestore'
import { buildFBRPayload, submitToFBR } from '@/services/fbr'
import { uploadInvoiceImage, uploadProductImage } from '@/services/cloudinary'
import { friendlyError } from '@/utils/friendlyError'
import { getLocalDateString } from '@/lib/utils'
import { FBRInvoice, InvoiceData } from '@/components/invoice/FBRInvoice'
import {
  Search, ShoppingCart, Plus, Minus, Trash2, User, CreditCard,
  Banknote, Package, ChevronDown, Tag, Building2, Smartphone, Barcode, FileText, Shield, X, Pencil, Check
} from 'lucide-react'

export const Route = createFileRoute('/admin/counter')({
  component: POSCounter,
})

interface CartItem {
  id: string; name: string; code: string; hsCode?: string
  price: number; quantity: number; taxRate: number; uom: string; maxStock: number
}
interface Product { id: string; name: string; code: string; hsCode?: string; taxRate?: number; price: number; stock: number; categoryId: string; unit: string; imageUrl?: string;[k: string]: any }
interface Category { id: string; name: string;[k: string]: any }
interface Customer { id: string; name: string; phone?: string; email?: string; ntn?: string; cnic?: string; address?: string; city?: string; type: string;[k: string]: any }

function POSCounter() {
  const { user } = useAuth()
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [cart, setCart] = useState<CartItem[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [showCustomerPicker, setShowCustomerPicker] = useState(false)
  const [customerSearch, setCustomerSearch] = useState('')
  const [walkInNtn, setWalkInNtn] = useState('')
  const [walkInStn, setWalkInStn] = useState('')
  const [walkInCnic, setWalkInCnic] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'credit_card' | 'debit_card' | 'bank_transfer'>('cash')
  const [discount, setDiscount] = useState(0)
  const [processing, setProcessing] = useState(false)
  const [showInvoice, setShowInvoice] = useState(false)
  const [generatedInvoice, setGeneratedInvoice] = useState<InvoiceData | null>(null)
  const [invoiceType, setInvoiceType] = useState<'general' | 'fbr'>('general')
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false)
  const [pendingFBRPayload, setPendingFBRPayload] = useState<any>(null)
  const [isSubmittingFBR, setIsSubmittingFBR] = useState(false)
  const [showCart, setShowCart] = useState(false)
  const [showTaxModal, setShowTaxModal] = useState(false)
  const [showCheckoutModal, setShowCheckoutModal] = useState(false)
  const [orderSaved, setOrderSaved] = useState(false) // Track if current invoice was already saved to DB
  const [savedOrderId, setSavedOrderId] = useState<string | null>(null)

  // Quick Add Form
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const [quickAddForm, setQuickAddForm] = useState({ name: '', price: '', hsCode: '', quantity: '1' })
  const [quickAddImage, setQuickAddImage] = useState<File | null>(null)
  const [isAddingQuick, setIsAddingQuick] = useState(false)

  // Cart Item Editing
  const [editingItemId, setEditingItemId] = useState<string | null>(null)

  // Invoice Metadata
  const [poNumber, setPoNumber] = useState('')
  const [deliveryNumber, setDeliveryNumber] = useState('')
  const [docNumber, setDocNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(getLocalDateString()) // yyyy-MM-dd

  // Company Settings
  const [companySettings, setCompanySettings] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS)

  useEffect(() => { loadData() }, [])
  const loadData = async () => {
    try {
      const [prods, cats, custs, company] = await Promise.all([
        getProducts(),
        getCategories(),
        getCustomers(),
        getCompanySettings(),
      ])
      setProducts(prods as Product[])
      setCategories(cats as Category[])
      setCustomers(custs as Customer[])
      setCompanySettings(company)
    } catch (err) { console.error('Load error:', err) }
    finally { setLoading(false) }
  }

  const filteredProducts = useMemo(() => products.filter((p) => {
    const s = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.code?.toLowerCase().includes(searchQuery.toLowerCase())
    const c = selectedCategory === 'all' || p.categoryId === selectedCategory
    return s && c
  }), [products, searchQuery, selectedCategory])

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const ex = prev.find((i) => i.id === product.id)
      if (ex) { return prev.map((i) => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i) }
      return [...prev, { id: product.id, name: product.name, code: product.code || product.id, hsCode: product.hsCode, price: product.price, quantity: 1, taxRate: product.taxRate ?? 18, uom: product.unit || 'PCS', maxStock: 999999 }]
    })
  }
  const updateQuantity = (id: string, d: number) => { 
    setCart(p => p.map(i => { 
      if (i.id !== id) return i; 
      let n = i.quantity + d; 
      // Handle floating point weirdness (e.g. 1.2 - 1 = 0.199999999)
      n = Math.round(n * 100) / 100;
      if (n <= 0) return i; 
      return { ...i, quantity: n };
    }));
  }
  const removeFromCart = (id: string) => { setCart(p => p.filter(i => i.id !== id)); if (editingItemId === id) setEditingItemId(null) }
  const clearCart = () => { setCart([]); setSelectedCustomer(null); setDiscount(0); setEditingItemId(null) }
  const updateCartItem = (id: string, field: 'name' | 'code' | 'price' | 'hsCode' | 'uom' | 'taxRate', value: string) => {
    setCart(p => p.map(i => {
      if (i.id !== id) return i
      if (field === 'price' || field === 'taxRate') return { ...i, [field]: parseFloat(value) || 0 }
      return { ...i, [field]: value }
    }))
  }

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0)
  const taxAmount = cart.reduce((s, i) => s + Math.round((i.price * i.quantity) * ((i.taxRate ?? 18) / 100)), 0)
  const totalAmount = subtotal - discount + taxAmount
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0)

  const handleAddCustomItem = async () => {
    if (!quickAddForm.name || !quickAddForm.price) return
    setIsAddingQuick(true)
    try {
      const price = parseFloat(quickAddForm.price)
      const qty = parseInt(quickAddForm.quantity) || 1
      const generatedCode = 'CUST-' + Math.floor(100000 + Math.random() * 900000)
      const defaultUom = 'KG'
      
      let imageUrl = ''
      if (quickAddImage) {
        imageUrl = await uploadProductImage(quickAddImage, generatedCode)
      }

      const newProductId = await createProduct({
        name: quickAddForm.name,
        code: generatedCode,
        hsCode: quickAddForm.hsCode || '8448.5900',
        price,
        costPrice: price,
        stock: 9999, // default large stock for custom items
        minStock: 10,
        categoryId: categories[0]?.id || 'misc',
        unit: defaultUom,
        taxRate: 18,
        imageUrl,
        isAvailable: true,
      })

      const newProduct: Product = {
        id: newProductId,
        name: quickAddForm.name,
        code: generatedCode,
        hsCode: quickAddForm.hsCode || '8448.5900',
        price,
        stock: 9999,
        categoryId: categories[0]?.id || 'misc',
        unit: defaultUom,
        taxRate: 18,
        imageUrl,
      }
      setProducts(prev => [newProduct, ...prev])

      setCart(prev => {
        const existing = prev.find(i => i.id === newProductId)
        if (existing) return prev.map(i => i.id === newProductId ? { ...i, quantity: i.quantity + qty } : i)
        return [...prev, {
          id: newProductId,
          name: newProduct.name,
          code: newProduct.code,
          hsCode: newProduct.hsCode,
          price,
          quantity: qty,
          taxRate: 18,
          uom: defaultUom,
          maxStock: 9999
        }]
      })

      setShowQuickAdd(false)
      setQuickAddForm({ name: '', price: '', hsCode: '', quantity: '1' })
      setQuickAddImage(null)
    } catch (error: any) {
      alert(friendlyError(error, 'Could not add item'))
    } finally {
      setIsAddingQuick(false)
    }
  }

  // Clear cart and all form state for a fresh new order
  const resetForNewOrder = () => {
    setCart([])
    setSelectedCustomer(null)
    setWalkInNtn('')
    setWalkInStn('')
    setWalkInCnic('')
    setDiscount(0)
    setPoNumber('')
    setDeliveryNumber('')
    setDocNumber('')
    setInvoiceDate(getLocalDateString())
    setOrderSaved(false)
    setSavedOrderId(null)
    setGeneratedInvoice(null)
    setPendingFBRPayload(null)
    setEditingItemId(null)
  }

  // Handle closing the invoice preview
  const handleInvoiceClose = async () => {
    if (orderSaved && savedOrderId) {
      // Order was already saved
      const startNew = confirm('Click OK to clear the cart and start a new order.\n\nClick Cancel to go back and edit your cart (This will safely void the recently complete order and return its stock so you can re-submit without errors).')
      if (startNew) {
        setShowInvoice(false)
        resetForNewOrder()
      } else {
        // User wants to go back and edit. Cancel the order in Firestore.
        try {
           setProcessing(true)
           await updateOrderStatus(savedOrderId, 'cancelled', 'User went back to edit cart data from invoice preview')
           setOrderSaved(false)
           setSavedOrderId(null)
           setShowInvoice(false)
           alert('The previous order was canceled and stock returned. You can now edit your items and checkout again.')
        } catch(err) {
           console.error(err)
           alert('Could not cancel order automatically. Please refresh or contact support.')
        } finally {
           setProcessing(false)
        }
      }
    } else {
      // Order not saved yet (preview only) — just go back to edit
      setShowInvoice(false)
    }
  }

  const handleCheckout = async () => {
    if (cart.length === 0) return
    setProcessing(true)
    try {
      // Validate HS codes for FBR invoices
      if (invoiceType === 'fbr') {
        const missingHS = cart.filter(i => !i.hsCode || i.hsCode === '0000.0000')
        if (missingHS.length > 0) {
          const names = missingHS.map(i => i.name).join(', ')
          if (!confirm(`⚠️ The following products are missing HS Codes:\n\n${names}\n\nFBR requires valid HS Codes for all items. A default code (8448.5900) will be used.\n\nContinue anyway?`)) {
            setProcessing(false)
            return
          }
        }
      }

      const finalOrderNumber = docNumber || await getNextInvoiceNumber()

      // Use the code the user set in the cart — it may have been manually edited
      const finalCartItems = cart.map(i => {
         return { ...i, displayCode: i.code || i.id.slice(0, 6) }
      })

      let fbrStatus: 'pending' | 'accepted' | 'none' = 'none'
      let fbrInvoiceNumber = ''
      let generatedFbrPayload: any = null

      if (invoiceType === 'fbr') {
        generatedFbrPayload = buildFBRPayload({
          invoiceNumber: finalOrderNumber,
          invoiceDate: invoiceDate,
          posId: companySettings.posId || '1',
          buyerNtn: selectedCustomer?.ntn || walkInNtn || undefined,
          buyerCnic: selectedCustomer?.cnic || walkInCnic || undefined,
          buyerName: selectedCustomer?.name || 'Walk-in Customer',
          buyerPhone: selectedCustomer?.phone,
          items: finalCartItems.map(i => ({
            code: i.displayCode,
            name: i.name,
            quantity: i.quantity,
            hsCode: i.hsCode || "8448.5900",
            uom: i.uom || "PCS",
            taxRate: i.taxRate ?? 18,
            unitPrice: i.price,
            totalAmount: i.price * i.quantity + Math.round(i.price * i.quantity * ((i.taxRate ?? 18) / 100)),
            taxAmount: Math.round(i.price * i.quantity * ((i.taxRate ?? 18) / 100)),
            discount: 0
          })),
          totalAmount,
          totalQuantity: totalItems,
          totalSaleValue: subtotal - discount,
          totalTax: taxAmount,
          discount,
          paymentMethod,
          sellerName: companySettings.companyName,
          sellerNTN: companySettings.ntn,
          sellerProvince: companySettings.province || 'Punjab',
          sellerAddress: `${companySettings.address}${companySettings.city ? `, ${companySettings.city}` : ''}`
        })
        setPendingFBRPayload(generatedFbrPayload)
        fbrStatus = 'pending'
      } else {
        setPendingFBRPayload(null)
      }

      // Save the order to Firestore WITH all the rich customer data so reports can show the identical invoice
      const newOrderId = await createOrder({
        orderNumber: finalOrderNumber,
        invoiceNumber: finalOrderNumber,
        invoiceType,
        invoiceDate: invoiceDate,
        date: invoiceDate,
        poNumber, deliveryNumber, docNumber: docNumber || undefined,
        customerId: selectedCustomer?.id || 'walk-in', 
        customerName: selectedCustomer?.name || 'Walk-in Customer',
        customerAddress: selectedCustomer?.address,
        customerCity: selectedCustomer?.city,
        customerNtn: selectedCustomer?.ntn || walkInNtn,
        customerCnic: selectedCustomer?.cnic || walkInCnic,
        customerStn: selectedCustomer?.stn || walkInStn,
        customerPhone: selectedCustomer?.phone,
        items: finalCartItems.map(i => ({ 
            productId: i.id, name: i.name, code: i.displayCode, hsCode: i.hsCode, uom: i.uom,
            quantity: i.quantity, unitPrice: i.price, total: i.price * i.quantity, 
            taxRate: i.taxRate ?? 18, taxAmount: Math.round(i.price * i.quantity * ((i.taxRate ?? 18) / 100)) 
        })),
        subtotal, taxRate: 0, taxAmount, discountAmount: discount, totalAmount, paymentMethod, status: 'completed', createdBy: user?.uid || '',
        fbrStatus: fbrStatus === 'none' ? undefined : fbrStatus,
        fbrPayload: generatedFbrPayload
      })

      // Mark order as saved so we don't save again
      setOrderSaved(true)
      setSavedOrderId(newOrderId)

      setGeneratedInvoice({
        invoiceNumber: finalOrderNumber,
        date: invoiceDate,
        poNumber, deliveryNumber, docNumber: docNumber || undefined,
        sellerName: companySettings.companyName,
        sellerAddress: companySettings.address,
        sellerCity: companySettings.city,
        sellerProvince: companySettings.province,
        sellerNtn: companySettings.ntn,
        sellerGst: companySettings.stn,
        sellerPhone: companySettings.phone || '',
        sellerEmail: companySettings.email || '',
        buyerName: selectedCustomer?.name || 'Walk-in Customer', buyerAddress: selectedCustomer?.address, buyerCity: selectedCustomer?.city, buyerNtn: selectedCustomer?.ntn || walkInNtn, buyerStn: selectedCustomer?.stn || walkInStn, buyerCnic: selectedCustomer?.cnic || walkInCnic, buyerPhone: selectedCustomer?.phone,
        items: finalCartItems.map((i, idx) => ({ serial: idx + 1, code: i.displayCode, product: i.name, hsCode: i.hsCode, saleType: 'Goods at standard rate', uom: i.uom || 'PCS', quantity: i.quantity, unitPrice: i.price, discount: 0, saleExTax: i.price * i.quantity, tax: Math.round(i.price * i.quantity * ((i.taxRate ?? 18) / 100)), subtotal: i.price * i.quantity + Math.round(i.price * i.quantity * ((i.taxRate ?? 18) / 100)) })),
        subtotal, taxRate: 0, taxAmount, discountAmount: discount, totalAmount,
        fbrInvoiceNumber: fbrInvoiceNumber || undefined, usid: invoiceType === 'fbr' ? finalOrderNumber : undefined, posId: invoiceType === 'fbr' ? (companySettings.posId || 'POS-001') : undefined, fbrStatus: fbrStatus === 'none' ? undefined : fbrStatus,
      })

      // Show the invoice preview — DO NOT clear cart here!
      // Cart stays intact so user can close the invoice and come back to edit
      setShowInvoice(true)

    } catch (err: any) {
      console.error("Critical Checkout Error:", err);
      alert(friendlyError(err, 'Checkout failed. Your data is preserved — please try again.'));
    }
    finally { setProcessing(false) }
  }

  // FBR Approval Handler
  const handleApproveFBR = async () => {
    if (!pendingFBRPayload || !generatedInvoice) return
    setIsSubmittingFBR(true)
    try {
      console.log("=== FBR SUBMISSION PAYLOAD ===", JSON.stringify(pendingFBRPayload, null, 2))
      console.log("=== savedOrderId ===", savedOrderId)
      console.log("=== FBR URL being used ===", import.meta.env.DEV ? '/fbr-api/postinvoicedata (via Vite proxy)' : 'https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata')
      const r = await submitToFBR(pendingFBRPayload)
      console.log("=== FBR FULL RESPONSE ===", JSON.stringify(r, null, 2))

      // ---- CRITICAL: Properly validate FBR response ----
      // FBR can return an Invoice_Id even for REJECTED invoices.
      // We MUST check validationResponse.status to confirm actual acceptance.
      // Let's grab both possible FBR IDs to ensure we aren't hiding the real one
      const fbrInternalId = r.Invoice_Id; // This is usually FBR's generated unique ID
      const fbrEchoedNumber = r.invoiceNumber; // This might be our own invoiceRefNo echoed back
      const fbrNumber = fbrInternalId || fbrEchoedNumber || null;
      
      const validationStatus = r.validationResponse?.status?.toLowerCase?.() || '';
      const validationStatusCode = r.validationResponse?.statusCode || '';
      const validationError = r.validationResponse?.error || '';
      const itemErrors = r.validationResponse?.invoiceStatuses?.map(
        (s: any) => `Item ${s.itemSNo}: ${s.error} (${s.status})`
      )?.join('\n') || '';

      // Log complete diagnostic info
      console.log("=== FBR DIAGNOSTICS ===");
      console.log("FBR Internal Invoice ID (Invoice_Id):", fbrInternalId);
      console.log("Echoed Invoice No (invoiceNumber):", fbrEchoedNumber);
      console.log("Primary FBR Number used:", fbrNumber);
      console.log("Validation Status:", validationStatus);
      console.log("Validation StatusCode:", validationStatusCode);
      console.log("Validation Error:", validationError);
      console.log("Item-level Errors:", itemErrors);
      console.log("Is Local Error:", r.isLocalError);
      console.log("Full response keys:", Object.keys(r));

      // Check for local errors first
      if (r.isLocalError) {
        alert(`❌ FBR Submission Failed (Local Error)\n\n${r.localErrorMessage}\n\nThe invoice was NOT submitted to FBR.`);
        return;
      }

      // Check for explicit rejection
      const isRejected = validationStatus === 'error' || 
        validationStatus === 'failed' || 
        validationStatusCode === '400' || 
        validationStatusCode === '401' ||
        validationStatusCode === '403' ||
        validationStatusCode === '500' ||
        (validationError && validationError.length > 0);

      if (fbrNumber && !isRejected) {
        // Update the local UI state immediately
        setGeneratedInvoice({ ...generatedInvoice, fbrInvoiceNumber: fbrNumber, fbrStatus: 'accepted' })
        
        // Now persist to Firestore
        if (savedOrderId) {
          try {
            await updateOrder(savedOrderId, { 
              fbrInvoiceNumber: fbrNumber, 
              fbrStatus: 'accepted',
            } as any);
            console.log("✅ FBR Invoice Number saved to Firestore:", fbrNumber, "for order:", savedOrderId);
            alert(
              `✅ FBR Invoice Submitted Successfully!\n\n` +
              `Important: To search on the IRIS portal, try using BOTH of these IDs:\n` +
              `1. FBR Internal ID: ${fbrInternalId || 'N/A'}\n` +
              `2. Invoice Ref No: ${fbrEchoedNumber || 'N/A'}\n\n` +
              `Validation Status: ${validationStatus || 'OK'}\n` +
              `Status Code: ${validationStatusCode || 'N/A'}\n` +
              `URL: ${(r as any)._debug?.url || 'N/A'}\n` +
              `Mode: ${(r as any)._debug?.mode || 'N/A'}\n` +
              `HTTP: ${(r as any)._debug?.httpStatus || 'N/A'}\n\n` +
              `Saved to database ✔`
            );
          } catch (saveErr: any) {
            console.error("❌ Failed to save FBR number to Firestore:", saveErr);
            alert(`⚠️ FBR accepted (Invoice #${fbrNumber}) but failed to save to database: ${saveErr.message}\n\nPlease screenshot this and contact support.`);
          }
        } else {
          console.error("❌ No savedOrderId available! Cannot save FBR number.");
          alert(`⚠️ FBR accepted (Invoice #${fbrNumber}) but no order ID was found to update.\n\nThis is a bug — the order might not have been saved before FBR submission.`);
        }
        
        console.log("FBR Output: Successfully Submitted. FBR Invoice #:", fbrNumber)
      } else {
        // FBR REJECTED the invoice or returned an error
        const diagnosticInfo = [
          `Invoice ID: ${fbrNumber || 'NONE'}`,
          `Status: ${validationStatus || 'N/A'}`,
          `Status Code: ${validationStatusCode || 'N/A'}`,
          `Error: ${validationError || 'N/A'}`,
          itemErrors ? `\nItem Errors:\n${itemErrors}` : '',
          `\nFull Response:\n${JSON.stringify(r, null, 2).substring(0, 500)}`
        ].filter(Boolean).join('\n');

        // Update status as rejected even if we got an ID
        if (savedOrderId) {
          try {
            await updateOrder(savedOrderId, { 
              fbrStatus: 'rejected',
              fbrError: validationError || itemErrors || 'Unknown FBR error',
            } as any);
          } catch {}
        }

        alert(
          `❌ FBR DID NOT ACCEPT THIS INVOICE\n\n` +
          `The FBR API returned an error. This invoice is NOT on the IRIS portal.\n\n` +
          diagnosticInfo
        );
      }
    } catch (err: any) {
      alert(friendlyError(err, 'FBR submission failed'))
    } finally {
      setIsSubmittingFBR(false)
    }
  }

  // Handle invoice image capture and cloud backup
  const handleInvoiceCaptured = async (blob: Blob) => {
    if (!generatedInvoice || isUploadingInvoice) return;
    try {
      setIsUploadingInvoice(true);
      const url = await uploadInvoiceImage(blob, generatedInvoice.invoiceNumber);
      console.log('Invoice saved to Cloudinary:', url);
      // Optional: Update the Firestore order document here to save the invoice URL
      // await updateOrder(generatedInvoice.invoiceNumber, { invoiceUrl: url });
      console.log(`Invoice ${generatedInvoice.invoiceNumber} saved to Secure Cloud`);
    } catch (err: any) {
      console.error('Invoice cloud backup failed:', err);
    } finally {
      setIsUploadingInvoice(false);
    }
  }

  const f = (n: number) => n.toLocaleString('en-PK')

  if (loading) return (
    <div className="flex h-screen overflow-hidden bg-[#f0f5ff] animate-pulse">
      <div className="flex-1 p-3"><div className="bg-white rounded-xl h-14 border border-slate-200/60 mb-3" /><div className="grid grid-cols-4 gap-2">{[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="bg-white rounded-xl h-28 border border-slate-200/60" />)}</div></div>
      <div className="w-[340px] bg-white border-l border-slate-200" />
    </div>
  )

  return (
    <div className="flex h-screen overflow-hidden bg-[#f0f5ff]">
      {/* Products Panel */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b border-slate-200/60 px-4 py-3">
          <div className="flex items-center gap-3 mb-2.5">
            {/* Spacer for mobile hamburger */}
            <div className="w-10 lg:hidden flex-shrink-0" />
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or code..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-[13px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-slate-50/50 transition-all"
              />
            </div>
            <div className="text-[10px] text-slate-500 bg-slate-100 px-2.5 py-1.5 rounded-lg font-mono font-semibold hidden sm:block">{filteredProducts.length} items</div>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1">
              <button onClick={() => setSelectedCategory('all')}
                className={`px-3.5 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all duration-200 ${selectedCategory === 'all' ? 'bg-blue-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                All Items
              </button>
              {categories.map(c => (
                <button key={c.id} onClick={() => setSelectedCategory(c.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all duration-200 ${selectedCategory === c.id ? 'bg-blue-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {c.name}
                </button>
              ))}
            </div>
            <button onClick={() => setShowQuickAdd(!showQuickAdd)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 hover:bg-indigo-700 transition-colors whitespace-nowrap shadow-sm">
              {showQuickAdd ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} Quick Add Item
            </button>
          </div>
        </div>

        {/* Quick Add Form Area */}
        {showQuickAdd && (
          <div className="bg-indigo-50/50 border-b border-indigo-100 p-3 shadow-inner">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-end gap-3 max-w-5xl">
              <div className="col-span-2 sm:flex-1 sm:min-w-[200px]">
                <label className="text-[10px] font-semibold text-indigo-800 mb-1 block">Item Name *</label>
                <input type="text" value={quickAddForm.name} onChange={e => setQuickAddForm({...quickAddForm, name: e.target.value})} placeholder="e.g. Custom Widget" className="w-full px-2.5 py-1.5 rounded-lg border border-indigo-200 text-[12px] focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div className="col-span-1 sm:w-24">
                <label className="text-[10px] font-semibold text-indigo-800 mb-1 block">Price *</label>
                <input type="number" min="0" value={quickAddForm.price} onChange={e => setQuickAddForm({...quickAddForm, price: e.target.value})} placeholder="0.00" className="w-full px-2.5 py-1.5 rounded-lg border border-indigo-200 text-[12px] focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div className="col-span-1 sm:w-20">
                <label className="text-[10px] font-semibold text-indigo-800 mb-1 block">Qty</label>
                <input type="number" min="1" value={quickAddForm.quantity} onChange={e => setQuickAddForm({...quickAddForm, quantity: e.target.value})} placeholder="1" className="w-full px-2.5 py-1.5 rounded-lg border border-indigo-200 text-[12px] focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div className="col-span-2 sm:col-span-1 sm:w-28">
                <label className="text-[10px] font-semibold text-indigo-800 mb-1 block">HS Code</label>
                <input type="text" value={quickAddForm.hsCode} onChange={e => setQuickAddForm({...quickAddForm, hsCode: e.target.value})} placeholder="8448.5900" className="w-full px-2.5 py-1.5 rounded-lg border border-indigo-200 text-[12px] focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[10px] font-semibold text-indigo-800 mb-1 block">Image (Opt)</label>
                <input type="file" accept="image/*" onChange={e => setQuickAddImage(e.target.files?.[0] || null)} className="w-[180px] text-[11px] text-indigo-600 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-[10px] file:font-semibold file:bg-indigo-100 file:text-indigo-700 hover:file:bg-indigo-200 cursor-pointer" />
              </div>
              <button disabled={!quickAddForm.name || !quickAddForm.price || isAddingQuick} onClick={handleAddCustomItem} className={`col-span-2 sm:col-span-1 px-4 py-1.5 rounded-lg text-[12px] font-bold text-white transition-all shadow-sm flex items-center justify-center gap-1.5 h-[34px] ${(!quickAddForm.name || !quickAddForm.price || isAddingQuick) ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                {isAddingQuick ? 'Adding...' : <><Plus className="w-3.5 h-3.5" /> Add to Cart</>}
              </button>
            </div>
          </div>
        )}

        {/* Product Grid */}
        <div className="flex-1 overflow-y-auto p-3">
          {filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Package className="w-12 h-12 text-slate-300 mb-3" />
              <h3 className="font-bold text-slate-700 text-sm">No Products</h3>
              <p className="text-slate-400 text-xs mt-1">{products.length === 0 ? 'Add products from Products page' : 'Try different filter'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {filteredProducts.map(product => {
                const inCart = cart.find(i => i.id === product.id)
                return (
                  <button key={product.id} onClick={() => addToCart(product)}
                    className={`bg-white rounded-xl border text-left flex flex-col overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 group
                      ${inCart ? 'border-blue-400 shadow-sm ring-1 ring-blue-200' : 'border-slate-200/80 hover:border-slate-300'}`}>

                    {/* Product Image Area */}
                    <div className="w-full h-24 bg-slate-100 flex items-center justify-center relative border-b border-slate-100">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-8 h-8 text-slate-300" />
                      )}

                      {/* Quantity Badge */}
                      {inCart && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-md border-2 border-white" style={{ width: '22px', height: '22px' }}>
                          {inCart.quantity}
                        </div>
                      )}
                    </div>

                    {/* Product Details Area */}
                    <div className="p-2.5 flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[9px] font-mono text-slate-400 flex items-center gap-0.5">
                          <Barcode className="w-2.5 h-2.5" />{product.code || product.id.slice(0, 6)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-800 text-[11px] leading-snug mb-2 line-clamp-2 flex-1">{product.name}</h3>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="font-bold text-slate-900 text-[12px]">Rs {f(product.price)}</span>
                        <span className={`text-[8.5px] font-semibold flex items-center gap-0.5 px-1.5 py-0.5 rounded ${product.stock <= 5 ? 'text-red-600 bg-red-50' : 'text-slate-500 bg-slate-50'}`}>
                          <Package className="w-2.5 h-2.5" />{product.stock}
                        </span>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cart Toggle Button */}
      {cart.length > 0 && (
        <button onClick={() => setShowCart(true)}
          className="fixed bottom-4 right-4 z-30 lg:hidden bg-blue-600 text-white px-5 py-3 rounded-2xl shadow-2xl shadow-blue-500/30 flex items-center gap-2 text-[13px] font-bold hover:bg-blue-700 transition-all active:scale-95">
          <ShoppingCart className="w-5 h-5" />
          <span>{totalItems}</span>
          <span className="text-blue-200">|</span>
          <span>Rs {f(totalAmount)}</span>
        </button>
      )}

      {/* Mobile Cart Overlay */}
      {showCart && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden" onClick={() => setShowCart(false)} />
      )}

      {/* Cart Panel */}
      <div className={`
        fixed top-0 right-0 z-50 h-full w-[85vw] max-w-[380px] bg-white border-l border-slate-200/60 flex flex-col overflow-hidden shadow-[-4px_0_20px_rgba(0,0,0,0.1)] transition-transform duration-300
        lg:static lg:w-[340px] lg:max-w-none lg:translate-x-0 lg:shadow-[-2px_0_8px_rgba(0,0,0,0.04)] lg:z-auto
        ${showCart ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Header */}
        <div className="px-4 py-3.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-slate-600" />
              <h2 className="font-bold text-slate-800 text-sm">Cart</h2>
              {totalItems > 0 && <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md">{totalItems}</span>}
            </div>
            <div className="flex items-center gap-2">
              {cart.length > 0 && <button onClick={clearCart} className="text-[10px] text-red-500 hover:text-red-600 font-semibold hover:bg-red-50 px-2 py-0.5 rounded-md transition-all">Clear</button>}
              <button onClick={() => setShowCart(false)} className="lg:hidden text-slate-400 hover:text-slate-600 p-1 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Customer */}
        <div className="px-4 py-2 border-b border-slate-100">
          <button onClick={() => setShowCustomerPicker(!showCustomerPicker)}
            className="w-full flex items-center gap-2.5 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200">
            <User className="w-4 h-4 text-slate-500" />
            <div className="flex-1 text-left">
              <div className="text-[12px] font-semibold text-slate-800">{selectedCustomer?.name || 'Walk-in Customer'}</div>
              {selectedCustomer?.phone && <div className="text-[10px] text-slate-400">{selectedCustomer.phone}</div>}
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
          {showCustomerPicker && (
            <div className="mt-1.5 bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 max-h-40 overflow-y-auto">
              <input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Search..." autoFocus
                className="w-full px-2.5 py-1.5 rounded border border-slate-200 text-[11px] mb-1.5 focus:outline-none focus:border-blue-500" />
              <button onClick={() => { setSelectedCustomer(null); setShowCustomerPicker(false) }}
                className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-50 text-[11px] font-medium text-slate-600">Walk-in Customer</button>
              {customers.filter(c => !customerSearch || c.name.toLowerCase().includes(customerSearch.toLowerCase())).map(c => (
                <button key={c.id} onClick={() => { setSelectedCustomer(c); setShowCustomerPicker(false) }}
                  className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-50 text-[11px]">
                  <div className="font-medium text-slate-800">{c.name}</div>
                  <div className="text-[9px] text-slate-400">{c.phone || c.type}</div>
                </button>
              ))}
            </div>
          )}
          {!selectedCustomer && (
            <button onClick={() => setShowTaxModal(true)}
              className="mt-2 w-full py-1.5 border border-dashed border-slate-300 rounded text-[10px] font-medium text-slate-500 hover:text-slate-700 hover:border-slate-400 hover:bg-slate-50 transition-colors">
              + Optional Tax Info (NTN/CNIC)
            </button>
          )}
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <ShoppingCart className="w-10 h-10 text-slate-200 mb-2" />
              <p className="text-slate-400 text-[11px]">Cart is empty</p>
            </div>
          ) : (
            <table className="w-full text-[11px]" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="text-slate-400 border-b border-slate-100">
                  <td className="py-1 font-semibold" style={{ width: '45%' }}>Item</td>
                  <td className="text-center py-1 font-semibold" style={{ width: '25%' }}>Qty</td>
                  <td className="text-right py-1 font-semibold" style={{ width: '20%' }}>Amount</td>
                  <td style={{ width: '10%' }}></td>
                </tr>
              </thead>
              <tbody>
                {cart.map(item => {
                  const isEditing = editingItemId === item.id

                  return (
                    <tr key={item.id} className={`border-b border-slate-50 transition-colors ${isEditing ? 'bg-blue-50/40' : 'hover:bg-slate-50/50'}`}>
                      {isEditing ? (
                        <td colSpan={4} className="py-2.5 px-2">
                          <div className="flex flex-col gap-1.5 w-full">
                            <input type="text" value={item.name} onChange={e => updateCartItem(item.id, 'name', e.target.value)}
                              className="w-full px-2 py-1.5 rounded-lg border border-blue-300 text-[11px] font-bold text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white shadow-sm" placeholder="Item name" />
                            
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                              <div>
                                <label className="text-[9px] text-slate-500 font-bold ml-0.5 tracking-wider uppercase">Code</label>
                                <input type="text" value={item.code || ''} onChange={e => updateCartItem(item.id, 'code', e.target.value)}
                                  className="w-full px-2 py-1.5 rounded text-[10px] border border-blue-200 text-slate-700 focus:outline-none focus:border-blue-500 bg-white" placeholder="Code" />
                              </div>
                              <div>
                                <label className="text-[9px] text-slate-500 font-bold ml-0.5 tracking-wider uppercase">Price</label>
                                <input type="number" value={item.price || ''} onChange={e => updateCartItem(item.id, 'price', e.target.value)}
                                  className="w-full px-2 py-1.5 rounded text-[10px] border border-blue-200 text-slate-700 focus:outline-none focus:border-blue-500 bg-white" placeholder="Rs" min="0" />
                              </div>
                              <div>
                                <label className="text-[9px] text-slate-500 font-bold ml-0.5 tracking-wider uppercase">HS Code</label>
                                <input type="text" value={item.hsCode || ''} onChange={e => updateCartItem(item.id, 'hsCode', e.target.value)}
                                  className="w-full px-2 py-1.5 rounded text-[10px] border border-blue-200 text-slate-700 focus:outline-none focus:border-blue-500 bg-white" placeholder="0000.0000" />
                              </div>
                              <div>
                                <label className="text-[9px] text-slate-500 font-bold ml-0.5 tracking-wider uppercase">GST %</label>
                                <input type="number" value={item.taxRate || ''} onChange={e => updateCartItem(item.id, 'taxRate', e.target.value)}
                                  className="w-full px-2 py-1.5 rounded text-[10px] border border-blue-200 text-slate-700 focus:outline-none focus:border-blue-500 bg-white" placeholder="18" min="0" />
                              </div>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end mt-1">
                              <div className="flex-1 w-full sm:w-auto">
                                <label className="text-[9px] text-slate-500 font-bold ml-0.5 tracking-wider uppercase">Unit of Measure</label>
                                <select value={item.uom} onChange={e => updateCartItem(item.id, 'uom', e.target.value)}
                                  className="w-full px-2 py-1.5 rounded border border-blue-200 text-[10px] font-medium text-slate-700 focus:outline-none focus:border-blue-500 bg-white">
                                  <option value="KG">KG (Kilograms)</option>
                                  <option value="PCS">PCS (Numbers, Pieces)</option>
                                  <option value="LTR">LTR (Litre)</option>
                                  <option value="MTR">MTR (Metre)</option>
                                  <option value="SQM">SQM (Square Metre)</option>
                                  <option value="CBM">CBM (Cubic Metre)</option>
                                  <option value="DOZEN">Dozens</option>
                                  <option value="PAIR">Pairs</option>
                                  <option value="PACK">Packs</option>
                                  <option value="CARAT">Carat</option>
                                  <option value="JOB">Job / Service</option>
                                </select>
                              </div>
                              <button onClick={() => setEditingItemId(null)} 
                                className="w-full sm:w-auto bg-blue-600 text-white rounded-lg px-4 py-1.5 text-[11px] font-bold hover:bg-blue-700 transition-colors shadow-sm flex items-center justify-center gap-1.5">
                                <Check className="w-3.5 h-3.5" /> Save
                              </button>
                            </div>
                          </div>
                        </td>
                      ) : (
                        <>
                          <td className="py-2.5">
                            <div className="font-semibold text-slate-800 text-[11px] truncate leading-tight" title={item.name}>{item.name}</div>
                            <div className="text-[9px] text-slate-400 mt-0.5 whitespace-nowrap" title={`Rs ${f(item.price)} · ${item.uom}${item.hsCode ? ` · ${item.hsCode}` : ''} · GST ${item.taxRate}%`}>
                              Rs {f(item.price)} · {item.uom}{item.hsCode ? ` · ${item.hsCode}` : ''} · GST {item.taxRate}%
                            </div>
                          </td>
                          <td className="py-2.5">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => updateQuantity(item.id, -1)}
                                className="w-5 h-5 bg-slate-100 hover:bg-slate-200 rounded flex items-center justify-center transition-all duration-150 active:scale-95">
                                <Minus className="w-2.5 h-2.5 text-slate-600" />
                              </button>
                              <input
                                type="number"
                                step="any"
                                min="0.01"
                                className="w-10 text-center font-bold text-slate-800 text-[11px] bg-transparent border-none focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none p-0"
                                value={item.quantity || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '') {
                                    setCart(p => p.map(i => i.id === item.id ? { ...i, quantity: 0 as any } : i));
                                    return;
                                  }
                                  const num = parseFloat(val);
                                  if (!isNaN(num) && num >= 0) {
                                    setCart(p => p.map(i => i.id === item.id ? { ...i, quantity: num } : i));
                                  }
                                }}
                                onBlur={() => {
                                  if (!item.quantity || item.quantity <= 0) {
                                    setCart(p => p.map(i => i.id === item.id ? { ...i, quantity: 1 } : i));
                                  }
                                }}
                              />
                              <button onClick={() => updateQuantity(item.id, 1)}
                                className="w-5 h-5 bg-slate-100 hover:bg-slate-200 rounded flex items-center justify-center transition-all duration-150 active:scale-95">
                                <Plus className="w-2.5 h-2.5 text-slate-600" />
                              </button>
                            </div>
                          </td>
                          <td className="py-2.5 text-right font-bold text-slate-800 pr-2">{f(item.price * item.quantity)}</td>
                          <td className="py-2.5 pl-1">
                            <div className="flex flex-col items-center gap-1">
                              <button onClick={() => setEditingItemId(item.id)}
                                className="p-1 text-slate-300 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                                title="Edit item">
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button onClick={() => removeFromCart(item.id)} 
                                className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Remove item">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Simple Totals & Proceed Bundle */}
        {cart.length > 0 && (
          <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 shrink-0">
            <div className="flex justify-between items-center mb-1 text-[11px] text-slate-500">
              <span>Subtotal</span>
              <span>Rs {f(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center mb-1 text-[11px] text-slate-500">
              <span>Sales Tax</span>
              <span>Rs {f(taxAmount)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between items-center mb-2 text-[11px] text-green-600">
                <span>Discount</span>
                <span>-Rs {f(discount)}</span>
              </div>
            )}
            <div className="flex justify-between items-center mt-2 mb-3 text-[14px] font-bold text-slate-800 border-t border-slate-200 pt-2">
              <span>Total</span>
              <span>Rs {f(totalAmount)}</span>
            </div>
            <button onClick={() => setShowCheckoutModal(true)}
              className="w-full py-3.5 rounded-xl font-bold text-white text-[13px] transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>

      {/* FBR Invoice Print Modal */}
      {showInvoice && generatedInvoice && (
        <FBRInvoice
          data={generatedInvoice}
          onClose={handleInvoiceClose}
          onInvoiceCaptured={handleInvoiceCaptured}
          onApprove={invoiceType === 'fbr' && generatedInvoice.fbrStatus === 'pending' ? handleApproveFBR : undefined}
          isSubmitting={isSubmittingFBR}
        />
      )}

      {/* Tax Info Modal */}
      {showTaxModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-sm">Walk-In Tax Details</h3>
              <button onClick={() => setShowTaxModal(false)} className="text-slate-400 hover:text-slate-600 rounded-lg p-1 hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex flex-col gap-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-600 block mb-1">NTN Number</label>
                <input type="text" value={walkInNtn} onChange={e => setWalkInNtn(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 block mb-1">STN Number</label>
                <input type="text" value={walkInStn} onChange={e => setWalkInStn(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 block mb-1">CNIC Number</label>
                <input type="text" value={walkInCnic} onChange={e => setWalkInCnic(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all" />
              </div>
              <button onClick={() => setShowTaxModal(false)} className="w-full mt-2 bg-slate-900 text-white font-semibold py-2.5 rounded-xl text-xs hover:bg-slate-800 transition-colors shadow-sm">
                Save & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 shrink-0">
              <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-blue-600" /> Complete Order
              </h3>
              <button onClick={() => setShowCheckoutModal(false)} className="text-slate-400 hover:text-slate-600 rounded-lg p-1 hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="overflow-y-auto p-4 flex flex-col gap-4">
              {/* References */}
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="col-span-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Doc Number</label>
                  <input type="text" value={docNumber} onChange={e => setDocNumber(e.target.value)} placeholder="Optional" className="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 bg-white shadow-sm" />
                </div>
                <div className="col-span-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Ref Number</label>
                  <input type="text" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="Optional" className="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 bg-white shadow-sm" />
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Delivery Number</label>
                  <input type="text" value={deliveryNumber} onChange={e => setDeliveryNumber(e.target.value)} placeholder="Optional" className="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 bg-white shadow-sm" />
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Invoice Date</label>
                  <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 bg-white shadow-sm" />
                </div>
              </div>

              {/* Payment Details */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-slate-400" /> Apply Discount</label>
                  <input type="number" value={discount || ''} onChange={e => setDiscount(Number(e.target.value) || 0)} placeholder="0" min={0} max={subtotal} className="w-24 px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-right focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 font-medium text-slate-800 shadow-sm" />
                </div>
                
                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-2 mb-1">Payment Method</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {([{ id: 'cash' as const, icon: Banknote, label: 'Cash' }, { id: 'debit_card' as const, icon: Smartphone, label: 'Debit' }, { id: 'credit_card' as const, icon: CreditCard, label: 'Credit' }, { id: 'bank_transfer' as const, icon: Building2, label: 'Bank' }]).map(({ id, icon: Icon, label }) => (
                    <button key={id} onClick={() => setPaymentMethod(id)}
                      className={`flex flex-col items-center gap-1 py-1.5 rounded-xl text-[10px] font-semibold border-2 transition-all ${paymentMethod === id ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm' : 'border-slate-100 text-slate-500 hover:border-slate-300 hover:bg-slate-50'}`}>
                      <Icon className="w-4 h-4" />{label}
                    </button>
                  ))}
                </div>

                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-3 mb-1">Invoice Type</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setInvoiceType('general')}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${invoiceType === 'general' ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm' : 'border-slate-100 text-slate-500 hover:border-slate-300 hover:bg-slate-50'}`}>
                    <FileText className="w-4 h-4" />General Invoice
                  </button>
                  <button onClick={() => setInvoiceType('fbr')}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${invoiceType === 'fbr' ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm' : 'border-slate-100 text-slate-500 hover:border-slate-300 hover:bg-slate-50'}`}>
                    <Shield className="w-4 h-4" />FBR Invoice
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0">
              <div className="flex items-end justify-between mb-4">
                <div className="text-slate-500 text-sm font-medium">Total Amount</div>
                <div className="text-2xl font-black text-slate-900 leading-none tracking-tight">Rs {f(totalAmount)}</div>
              </div>
              <button 
                onClick={() => { setShowCheckoutModal(false); handleCheckout(); }} 
                disabled={processing}
                className="w-full py-3.5 rounded-xl font-bold text-white text-[14px] transition-all duration-200 disabled:opacity-50 hover:shadow-lg hover:shadow-blue-500/30 active:scale-[0.98] shadow-md flex items-center justify-center gap-2"
                style={{ background: processing ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                {processing ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing...</>
                ) : (
                  <><Check className="w-5 h-5" /> Complete Checkout</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}